import { selectService } from '@/services/map/selectService';
import GeoJSON from 'ol/format/GeoJSON';
import Draw from 'ol/interaction/Draw';
import { createBox } from 'ol/interaction/Draw';
import { fromCircle } from 'ol/geom/Polygon';
import { Circle, Point } from 'ol/geom';
import Feature from 'ol/Feature';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style';
import * as turf from '@turf/turf';

class GeoprocessingSelectionHandler {
    constructor() {
        this.drawInteraction = null;
        this.currentLayer = null;
        this.activeFeatures = []; // Store original features separate from visualization artifacts

        // Setup Highlight Layer
        this.highlightSource = new VectorSource();
        this.highlightLayer = new VectorLayer({
            source: this.highlightSource,
            zIndex: 9999,
            style: new Style({
                stroke: new Stroke({
                    color: 'rgba(0, 255, 255, 1)', // Cyan stroke
                    width: 3
                }),
                fill: new Fill({
                    color: 'rgba(0, 255, 255, 0.2)' // Cyan fill translucent
                }),
                image: new CircleStyle({
                    radius: 6,
                    fill: new Fill({ color: 'rgba(0, 255, 255, 0.4)' }),
                    stroke: new Stroke({ color: 'cyan', width: 2 })
                })
            })
        });
    }

    startSelection(map, layer, influenceType, onFeaturesSelected, accumulate = false, options = {}) {
        if (!map) return;
        this.currentLayer = layer;
        this.accumulate = accumulate;
        this.currentInfluenceType = influenceType;
        this.accumulate = accumulate;
        this.currentInfluenceType = influenceType;
        this.currentOptions = options;
        this.onSelectionChange = onFeaturesSelected; // Store callback for updates

        // Ensure highlight layer is added
        if (!map.getLayers().getArray().includes(this.highlightLayer)) {
            map.addLayer(this.highlightLayer);
        }

        // Check if selection layer exists, if not, verify if we can init it
        if (!selectService.selectionLayer) {
            selectService.createSelectionLayer();
        }

        // Cleanup existing interactions
        this.stopSelection(map);

        let drawType = 'Polygon';
        let geometryFunction = null;

        // User requested that selection should ALWAYS be a square/box, regardless of influence type
        // So we default to Circle+createBox for all cases.
        drawType = 'Circle';
        geometryFunction = createBox();

        // if (influenceType === 'Poligonal') { ... } // Logic removed to enforce box everywhere
        // We can add other types later (e.g. Lineal -> LineString, Radial -> Point/Circle)

        this.drawInteraction = new Draw({
            source: selectService.selectionLayer.getSource(),
            type: drawType,
            geometryFunction: geometryFunction
        });

        this.drawInteraction.on('drawend', async (evt) => {
            let geometry = evt.feature.getGeometry();

            // Handle Box created via createBox (which returns a Polygon)
            // But if it was a circle without geometryFunction it would need casting
            if (drawType === 'Circle' && !geometryFunction) {
                geometry = fromCircle(geometry);
            }

            // Query features (Returns POJOs now)
            const featuresPOJO = await selectService.getAreaFeatures(geometry, [this.currentLayer]);

            // Convert POJOs to OL Features
            const format = new GeoJSON();
            const features = featuresPOJO.map(f => format.readFeature(f));

            // Re-attach layerName since readFeature might drop custom properties not in properties keys
            features.forEach((olFeature, index) => {
                const original = featuresPOJO[index];
                if (original.layerName) {
                    olFeature.set('layerName', original.layerName);
                }
            });

            // Highlight features
            this.highlightFeatures(features, this.accumulate);

            // Clear the selection box (blue rectangle) from the map
            if (selectService.selectionLayer) {
                selectService.selectionLayer.getSource().clear();
            }

            if (this.onSelectionChange) {
                // Initial notification
                this.notifySelection();
            }
        });

        // Add selection layer if not present
        if (!map.getLayers().getArray().includes(selectService.selectionLayer)) {
            map.addLayer(selectService.selectionLayer);
        }

        map.addInteraction(this.drawInteraction);
    }

    stopSelection(map) {
        if (this.drawInteraction) {
            map.removeInteraction(this.drawInteraction);
            this.drawInteraction = null;
        }
        if (selectService.selectionLayer) {
            selectService.selectionLayer.getSource().clear();
        }
    }

    highlightFeatures(features, accumulate = false) {
        // Update State
        if (!accumulate) {
            this.activeFeatures = [];
        }

        if (features && features.length > 0) {
            if (accumulate) {
                // Deduplicate: Filter out activeFeatures that already exist
                const newFeatures = features.filter(newF => {
                    return !this.activeFeatures.some(activeF => this.areFeaturesEqual(newF, activeF));
                });
                this.activeFeatures = this.activeFeatures.concat(newFeatures);
            } else {
                this.activeFeatures = features;
            }
        }

        this.renderHighlights();
    }

    areFeaturesEqual(f1, f2) {
        // 1. Try ID match
        const id1 = f1.getId();
        const id2 = f2.getId();
        if (id1 && id2) {
            return id1 === id2;
        }

        // 2. Try Geometry match (Coordinates)
        // This is expensive but necessary if no IDs
        const g1 = f1.getGeometry();
        const g2 = f2.getGeometry();

        if (g1 && g2 && g1.getType() === g2.getType()) {
            const c1 = g1.getCoordinates();
            const c2 = g2.getCoordinates();
            return JSON.stringify(c1) === JSON.stringify(c2);
        }

        return false;
    }

    updateOptions(options) {
        this.currentOptions = { ...this.currentOptions, ...options };
        this.renderHighlights();
        this.notifySelection();
    }

    renderHighlights() {
        this.highlightSource.clear();

        if (this.activeFeatures.length === 0) return;

        console.log("💎 renderHighlights. Dissolve:", this.currentOptions && this.currentOptions.dissolve);

        // Add original features
        this.highlightSource.addFeatures(this.activeFeatures);

        // Radial Visualization
        if (this.currentInfluenceType === 'Radial' &&
            this.currentOptions &&
            this.currentOptions.distancias &&
            this.currentOptions.distancias.length > 0) {

            if (this.currentOptions && this.currentOptions.dissolve) {
                console.log("💎 Intentando disolver en Frontend...");
                let allPolygons = [];
                const format = new GeoJSON();

                this.activeFeatures.forEach(feature => {
                    const geometry = feature.getGeometry();
                    if (geometry && geometry.getType() === 'Point') {
                        const center = geometry.getCoordinates();
                        this.currentOptions.distancias.forEach(dist => {
                            const radiusMeters = dist;
                            const circleGeom = new Circle(center, radiusMeters);
                            const polyGeom = fromCircle(circleGeom);
                            const polyFeature = new Feature(polyGeom);
                            allPolygons.push(format.writeFeatureObject(polyFeature, {
                                dataProjection: 'EPSG:4326',
                                featureProjection: 'EPSG:3857'
                            }));
                        });
                    }
                });

                console.log(`💎 Polígonos a unir: ${allPolygons.length}`);

                if (allPolygons.length > 0) {
                    try {
                        let unioned = null;
                        if (allPolygons.length === 1) {
                            unioned = allPolygons[0];
                        } else {
                            unioned = turf.union(turf.featureCollection(allPolygons));
                        }

                        if (unioned) {
                            console.log("✅ Union exitosa en Frontend (Turf 7)");
                            const dissolvedFeature = format.readFeature(unioned, {
                                dataProjection: 'EPSG:4326',
                                featureProjection: 'EPSG:3857'
                            });
                            dissolvedFeature.setStyle(new Style({
                                stroke: new Stroke({ color: 'rgba(0, 255, 255, 1)', width: 3 }),
                                fill: new Fill({ color: 'rgba(0, 255, 255, 0.4)' })
                            }));
                            this.highlightSource.addFeature(dissolvedFeature);
                        }
                    } catch (e) {
                        console.error("❌ Error en union de Turf:", e);
                        this.renderIndividualRings();
                    }
                }
            } else {
                this.renderIndividualRings();
            }
        }
    }

    renderIndividualRings() {
        this.activeFeatures.forEach(feature => {
            const geometry = feature.getGeometry();
            if (geometry && geometry.getType() === 'Point') {
                const center = geometry.getCoordinates();

                this.currentOptions.distancias.forEach(dist => {
                    const radiusMeters = dist;
                    const circleGeom = new Circle(center, radiusMeters);
                    const circleFeature = new Feature(circleGeom);

                    // Apply specific style for radial circles: Transparent fill
                    circleFeature.setStyle(new Style({
                        stroke: new Stroke({
                            color: 'rgba(0, 255, 255, 1)',
                            width: 2
                        }),
                        fill: new Fill({
                            color: 'rgba(0, 255, 255, 0.05)' // Very slight, almost transparent fill
                        })
                    }));

                    this.highlightSource.addFeature(circleFeature);
                });
            }
        });
    }

    notifySelection() {
        const details = [];
        const allHighlightFeatures = this.highlightSource.getFeatures();

        console.log("--- Selection Update ---");

        if (this.currentInfluenceType === 'Radial' && this.currentOptions?.distancias?.length > 0) {
            console.log("Mode: Radial Influence");
            this.activeFeatures.forEach(feature => {
                const geom = feature.getGeometry();
                if (geom.getType() === 'Point') {
                    const coords = geom.getCoordinates();
                    console.log(`Center Point: ${coords}`);

                    const circles = [];
                    this.currentOptions.distancias.forEach(dist => {
                        const distMeters = dist;
                        circles.push({
                            radiusMeters: distMeters,
                            // Note: To get the actual circle perimeter coords would require more math or converting the Circle geometry 
                            // we generated in renderHighlights, but logic-wise this is the definition.
                        });
                        console.log(`  -> Ring Radius: ${dist}km (${distMeters}m)`);
                    });
                    details.push({ center: coords, rings: circles });
                }
            });
        } else {
            console.log("Mode: Standard Selection");
            this.activeFeatures.forEach(f => {
                const geom = f.getGeometry();
                const coords = geom.getCoordinates();
                console.log(`Feature Type: ${geom.getType()}, Coords:`, coords);
                details.push({ type: geom.getType(), coordinates: coords });
            });
        }

        if (this.onSelectionChange) {
            // Pass both the View Features (highlights) and the Data Details
            this.onSelectionChange(allHighlightFeatures, details);
        }
    }

    clearHighlight() {
        this.activeFeatures = [];
        this.highlightSource.clear();
        this.notifySelection();
    }
}

export const geoprocessingSelectionHandler = new GeoprocessingSelectionHandler();
